/**
 * EmployeeAdvancesPage — Issue #370.
 *
 * 借支单列表 + 创建 + 审批 + 出纳付款。
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { FINANCE_PERMISSIONS, hasPermission } from '@/lib/financePermissions';
import {
  useEmployeeAdvanceActions,
  useEmployeeAdvances,
  type CreateEmployeeAdvanceDto,
  type EmployeeAdvanceSummaryDto,
} from '@/hooks/useFinanceSettlement';

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  pending_department_approval: '待部门审核',
  pending_finance_review: '待财务复核',
  pending_cashier_execution: '待出纳付款',
  outstanding: '已付款 / 待核销',
  settled: '已核销',
  rejected: '已驳回',
  cancelled: '已撤回',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending_department_approval: 'bg-sky-100 text-sky-700',
  pending_finance_review: 'bg-blue-100 text-blue-700',
  pending_cashier_execution: 'bg-purple-100 text-purple-800',
  outstanding: 'bg-amber-100 text-amber-800',
  settled: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-500',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function emptyDraft(): CreateEmployeeAdvanceDto {
  return {
    advanceNo: '',
    advanceDate: new Date().toISOString().slice(0, 10),
    departmentName: '',
    employeeUserId: '',
    employeeName: '',
    reason: '',
    amount: '',
  };
}

export default function EmployeeAdvancesPage() {
  const { permissions } = useAuth();
  const granted = permissions ?? [];
  const canWrite = hasPermission(
    granted,
    FINANCE_PERMISSIONS.EMPLOYEE_ADVANCE_WRITE,
  );
  const canDepartmentApprove = hasPermission(
    granted,
    FINANCE_PERMISSIONS.EMPLOYEE_ADVANCE_DEPARTMENT_APPROVE,
  );
  const canFinanceReview = hasPermission(
    granted,
    FINANCE_PERMISSIONS.EMPLOYEE_ADVANCE_FINANCE_REVIEW,
  );
  const canCashier = hasPermission(
    granted,
    FINANCE_PERMISSIONS.EMPLOYEE_ADVANCE_CASHIER_EXECUTE,
  );
  const canManage = hasPermission(
    granted,
    FINANCE_PERMISSIONS.EMPLOYEE_ADVANCE_MANAGE,
  );

  const { data, loading, error, fetchList } = useEmployeeAdvances();
  const actions = useEmployeeAdvanceActions();

  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [draft, setDraft] = useState<CreateEmployeeAdvanceDto>(emptyDraft());
  const [creating, setCreating] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  useEffect(() => {
    void fetchList({ status: statusFilter || undefined, search: searchTerm || undefined });
  }, [fetchList, statusFilter, searchTerm]);

  const rows = useMemo(() => data?.rows ?? [], [data]);

  const submitDraft = async () => {
    if (!canWrite) return;
    setActionMessage(null);
    const result = await actions.create(draft);
    if (result) {
      setActionMessage(`已创建借支单：${result.advanceNo}`);
      setDraft(emptyDraft());
      setCreating(false);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleSubmit = async (row: EmployeeAdvanceSummaryDto) => {
    setActionMessage(null);
    const result = await actions.submit(row.id, row.recordVersion);
    if (result) {
      setActionMessage(`${result.advanceNo} 已提交`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleDepartmentReview = async (row: EmployeeAdvanceSummaryDto) => {
    setActionMessage(null);
    const result = await actions.departmentReview(row.id, {
      expectedRecordVersion: row.recordVersion,
    });
    if (result) {
      setActionMessage(`${result.advanceNo} 部门审核通过`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleFinanceReview = async (row: EmployeeAdvanceSummaryDto) => {
    setActionMessage(null);
    const result = await actions.financeReview(row.id, {
      expectedRecordVersion: row.recordVersion,
    });
    if (result) {
      setActionMessage(`${result.advanceNo} 财务复核通过`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleCashier = async (row: EmployeeAdvanceSummaryDto) => {
    setActionMessage(null);
    const result = await actions.cashierExecute(row.id, {
      expectedRecordVersion: row.recordVersion,
      paymentMethod: 'bank_transfer',
      executedAt: new Date().toISOString().slice(0, 10),
    });
    if (result) {
      setActionMessage(`${result.advanceNo} 出纳已付款`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const promptReason = (prefix: string): string | null => {
    const reason = window.prompt(`${prefix}原因（必填）`);
    if (reason === null) return null;
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setActionMessage(null);
      return null;
    }
    return trimmed;
  };

  const handleDepartmentReject = async (row: EmployeeAdvanceSummaryDto) => {
    const reason = promptReason('部门审核驳回');
    if (!reason) return;
    setActionMessage(null);
    const result = await actions.departmentReject(row.id, {
      expectedRecordVersion: row.recordVersion,
      reason,
    });
    if (result) {
      setActionMessage(`${result.advanceNo} 已被部门驳回`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleFinanceReject = async (row: EmployeeAdvanceSummaryDto) => {
    const reason = promptReason('财务复核驳回');
    if (!reason) return;
    setActionMessage(null);
    const result = await actions.financeReject(row.id, {
      expectedRecordVersion: row.recordVersion,
      reason,
    });
    if (result) {
      setActionMessage(`${result.advanceNo} 已被财务驳回`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleCancel = async (row: EmployeeAdvanceSummaryDto) => {
    const reason = promptReason('撤回');
    if (!reason) return;
    setActionMessage(null);
    const result = await actions.cancel(row.id, {
      expectedRecordVersion: row.recordVersion,
      reason,
    });
    if (result) {
      setActionMessage(`${result.advanceNo} 已撤回`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleReturnToDraft = async (row: EmployeeAdvanceSummaryDto) => {
    setActionMessage(null);
    const result = await actions.returnToDraft(row.id, row.recordVersion);
    if (result) {
      setActionMessage(`${result.advanceNo} 已回到草稿`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const canCancel = (row: EmployeeAdvanceSummaryDto): boolean => {
    if (!canManage) return false;
    return (
      row.status === 'draft' ||
      row.status === 'pending_department_approval' ||
      row.status === 'pending_finance_review' ||
      row.status === 'pending_cashier_execution'
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">借支单</h1>
        {canWrite ? (
          <button
            onClick={() => setCreating((v) => !v)}
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground shadow-sm"
          >
            {creating ? '取消' : '新建'}
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          className="rounded border px-2 py-1 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">全部状态</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <input
          type="text"
          className="rounded border px-2 py-1 text-sm"
          placeholder="搜索单号 / 部门 / 借支人"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {actionMessage ? (
        <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          {actionMessage}
        </div>
      ) : null}
      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      ) : null}
      {actions.error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {actions.error.message}
        </div>
      ) : null}

      {creating ? (
        <div className="space-y-3 rounded border bg-white p-4 shadow-sm">
          <h2 className="text-base font-semibold">新建借支单</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">单号</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.advanceNo}
                onChange={(e) => setDraft({ ...draft, advanceNo: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">日期</span>
              <input
                type="date"
                className="rounded border px-2 py-1"
                value={draft.advanceDate}
                onChange={(e) => setDraft({ ...draft, advanceDate: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">部门</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.departmentName}
                onChange={(e) => setDraft({ ...draft, departmentName: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">借支人 UserId</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.employeeUserId}
                onChange={(e) => setDraft({ ...draft, employeeUserId: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">借支人姓名</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.employeeName}
                onChange={(e) => setDraft({ ...draft, employeeName: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">职务</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.employeePosition ?? ''}
                onChange={(e) => setDraft({ ...draft, employeePosition: e.target.value || undefined })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">借支金额</span>
              <input
                type="text"
                inputMode="decimal"
                className="rounded border px-2 py-1"
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">金额大写</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.amountInWords ?? ''}
                onChange={(e) => setDraft({ ...draft, amountInWords: e.target.value || undefined })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm md:col-span-2">
              <span className="text-gray-600">借支事由</span>
              <textarea
                rows={2}
                className="rounded border px-2 py-1"
                value={draft.reason}
                onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm md:col-span-2">
              <span className="text-gray-600">备注</span>
              <textarea
                rows={2}
                className="rounded border px-2 py-1"
                value={draft.remarks ?? ''}
                onChange={(e) => setDraft({ ...draft, remarks: e.target.value || undefined })}
              />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setCreating(false);
                setDraft(emptyDraft());
              }}
              className="rounded border px-3 py-1.5 text-sm"
            >
              取消
            </button>
            <button
              onClick={submitDraft}
              disabled={actions.loading}
              className="rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground"
            >
              {actions.loading ? '提交中…' : '保存草稿'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded border bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">单号 / 日期</th>
              <th className="px-3 py-2 text-left">部门 / 借支人</th>
              <th className="px-3 py-2 text-right">借支金额</th>
              <th className="px-3 py-2 text-right">未核销余额</th>
              <th className="px-3 py-2 text-left">状态</th>
              <th className="px-3 py-2 text-left">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-3 py-4 text-center text-gray-500" colSpan={6}>
                  加载中…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="px-3 py-4 text-center text-gray-500" colSpan={6}>
                  暂无数据
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="font-medium">{row.advanceNo}</div>
                    <div className="text-xs text-gray-500">{row.advanceDate}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div>{row.departmentName}</div>
                    <div className="text-xs text-gray-500">{row.employeeName}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{row.amount}</td>
                  <td className="px-3 py-2 text-right font-mono">{row.outstandingBalance}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={row.status} />
                  </td>
                  <td
                    className="space-x-1 px-3 py-2"
                    data-testid={`employee-advance-row-actions-${row.id}`}
                  >
                    {row.status === 'draft' && canWrite ? (
                      <button
                        data-testid={`employee-advance-action-submit-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleSubmit(row)}
                      >
                        提交
                      </button>
                    ) : null}
                    {row.status === 'pending_department_approval' && canDepartmentApprove ? (
                      <button
                        data-testid={`employee-advance-action-department-review-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleDepartmentReview(row)}
                      >
                        部门审核
                      </button>
                    ) : null}
                    {row.status === 'pending_department_approval' && canDepartmentApprove ? (
                      <button
                        data-testid={`employee-advance-action-department-reject-${row.id}`}
                        className="rounded border border-red-300 px-2 py-1 text-xs text-red-700"
                        onClick={() => handleDepartmentReject(row)}
                      >
                        部门驳回
                      </button>
                    ) : null}
                    {row.status === 'pending_finance_review' && canFinanceReview ? (
                      <button
                        data-testid={`employee-advance-action-finance-review-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleFinanceReview(row)}
                      >
                        财务复核
                      </button>
                    ) : null}
                    {(row.status === 'pending_finance_review' ||
                      row.status === 'pending_cashier_execution') &&
                    canFinanceReview ? (
                      <button
                        data-testid={`employee-advance-action-finance-reject-${row.id}`}
                        className="rounded border border-red-300 px-2 py-1 text-xs text-red-700"
                        onClick={() => handleFinanceReject(row)}
                      >
                        财务驳回
                      </button>
                    ) : null}
                    {row.status === 'pending_cashier_execution' && canCashier ? (
                      <button
                        data-testid={`employee-advance-action-cashier-execute-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleCashier(row)}
                      >
                        出纳付款
                      </button>
                    ) : null}
                    {canCancel(row) ? (
                      <button
                        data-testid={`employee-advance-action-cancel-${row.id}`}
                        className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700"
                        onClick={() => handleCancel(row)}
                      >
                        撤回
                      </button>
                    ) : null}
                    {row.status === 'rejected' && canWrite ? (
                      <button
                        data-testid={`employee-advance-action-return-to-draft-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleReturnToDraft(row)}
                      >
                        回到草稿
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
